/**
 * CoryTips — demo-only "Cory suggests" card with QSR-tailored talking points.
 * Each tip is clickable and jumps to the relevant tab / lever so the advice
 * becomes a one-tap action during a walkthrough.
 *
 * Tip action fields (any one):
 *   tab      -> QSR dashboard tab id; used with onAction (module context)
 *   href     -> route to navigate to (page context, e.g. /app/dashboard?tab=delivery)
 *   scrollTo -> CSS selector to smooth-scroll to on the same page (e.g. #pickup-lever)
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

export const CORY_TIPS = {
  financials: [
    { t: 'Shift 8% of delivery to direct pickup', d: 'Avoids aggregator commission. Launch the pickup loyalty offer — projected ~+$142k/yr network margin as pickup adoption ramps.', cta: 'Open pickup lever', tab: 'delivery', href: '/app/dashboard?tab=delivery#pickup-lever' },
    { t: 'Footscray food cost is 1.9pts above target', d: 'Portioning drift on Loaded Fries. Tighten the spec and recover ~$18k/yr at that store alone.', cta: 'View waste & voids', tab: 'waste', href: '/app/dashboard?tab=waste' },
    { t: 'Penrith labour runs 2pts high on Friday dinners', d: 'One crew overlap on the 6-8pm peak. Trim it and save ~$9k/yr without hurting speed.', cta: 'Open Labour & SPLH', tab: 'labour', href: '/app/dashboard?tab=labour' },
  ],
  marketing: [
    { t: 'Reply to 41 unanswered Google reviews', d: 'Response rate 88% -> 95% lifts local pack ranking across all 5 suburbs this week.', cta: 'Ask Cory to draft replies', tab: 'insights', href: '/app/dashboard?tab=insights' },
    { t: 'Southport Uber Eats rating dipped to 4.3', d: '"Cold fries" is the top complaint — add a hot-hold check at handover to recover the star.', cta: 'Open Speed of Service', tab: 'speed', href: '/app/dashboard?tab=speed' },
    { t: 'Run a "direct pickup 2-for-1" push', d: 'Pulls orders off aggregators and cuts the 27% commission drag — pairs with the Financials pickup lever.', cta: 'Open pickup lever', tab: 'delivery', href: '/app/dashboard?tab=delivery#pickup-lever' },
  ],
  speed: [
    { t: 'Southport drive-thru is 1.1 min over target', d: 'Bottleneck is the 12-1pm window. Add a second order-taker on Fri/Sat lunch to pull SOS back under 3.5 min.', cta: 'Open Speed Coach', tab: 'speedcoach', href: '/app/dashboard?tab=speedcoach' },
    { t: 'Order accuracy at Prospect is 93.1%', d: 'Below the 95% target — most misses are on Double Stack customisations. A KDS confirm-back step lifts accuracy ~2pts.', cta: 'Open Speed Coach', tab: 'speedcoach', href: '/app/dashboard?tab=speedcoach' },
    { t: 'Footscray peaks at 118 cars/hr', d: 'Pre-stage Classic Grill Burgers 15 min before the lunch rush to shave ~20s off average wait.', cta: 'View sales & menu mix', tab: 'sales', href: '/app/dashboard?tab=sales' },
  ],
  operations: [
    { t: 'Southport labour is 4.7pts over target', d: 'SPLH is $63.9 vs $75 target. Trim one crew overlap on the mid-afternoon lull to recover ~$9k/yr without hurting speed.', cta: 'Open Speed Coach', tab: 'speedcoach', href: '/app/dashboard?tab=speedcoach' },
    { t: 'Penrith Friday dinner is understaffed for demand', d: 'Sales spike but SPLH holds — add 0.5 FTE on the 6-8pm peak to protect speed and accuracy.', cta: 'View Speed of Service', tab: 'speed', href: '/app/dashboard?tab=speed' },
    { t: 'Waste is trending down network-wide', d: 'Prep over-portioning is still the #1 driver at Footscray — tighten the Loaded Fries spec to hold the gains.', cta: 'Open waste & voids', tab: 'waste', href: '/app/dashboard?tab=waste' },
  ],
  sales: [
    { t: 'Combo attach is 48% at Southport vs 62% at Footscray', d: 'A "make it a combo" upsell prompt on the KDS/register could add ~$0.90 to average check network-wide.', cta: 'Open pickup lever', tab: 'delivery', href: '/app/dashboard?tab=delivery' },
    { t: 'Loaded Fries is your #2 seller but drives waste', d: 'Tighten the portion spec and bundle into the Double Stack combo to lift margin without losing volume.', cta: 'View waste & voids', tab: 'waste', href: '/app/dashboard?tab=waste' },
    { t: 'Kids Grill Box under-indexes at drive-thru stores', d: 'Add a kids-meal call-out on the drive-thru menu board — small mix shift, meaningful weekend uplift.', cta: 'Open Speed of Service', tab: 'speed', href: '/app/dashboard?tab=speed' },
  ],
  delivery: [
    { t: 'Aggregators take ~27% commission on 30% of sales', d: 'Every order you convert to direct pickup keeps that margin. Use the slider below to model the uplift.', cta: 'Jump to pickup lever', scrollTo: '#pickup-lever' },
    { t: 'Southport delivery margin is only 8%', d: 'Highest aggregator mix (41%) in the network. A pickup loyalty offer here has the biggest payback.', cta: 'Jump to pickup lever', scrollTo: '#pickup-lever' },
    { t: 'Launch a pickup loyalty offer', d: 'Nudges the slider to an 8% shift and simulates the offer — projected ~+$142k/yr network margin as pickup adoption ramps. One tap.', cta: 'Jump to pickup lever', scrollTo: '#pickup-lever' },
  ],
};

const VARIANT = {
  default: { card: 'glass border-primary/30 rounded-2xl shadow-liquid-md', title: 'text-foreground', sub: 'text-muted-foreground', row: 'bg-primary/5 hover:bg-primary/10', rowTitle: 'text-foreground', rowDesc: 'text-muted-foreground', icon: 'text-primary', badge: 'bg-primary/15', cta: 'text-primary' },
  qsr: { card: 'bg-white border border-slate-200 rounded-2xl shadow-sm', title: 'text-slate-900', sub: 'text-slate-500', row: 'bg-emerald-50 hover:bg-emerald-100', rowTitle: 'text-slate-900', rowDesc: 'text-slate-500', icon: 'text-emerald-600', badge: 'bg-emerald-500/15', cta: 'text-emerald-700' },
};

export default function CoryTips({ page = 'financials', variant = 'default', onAction }) {
  const tips = CORY_TIPS[page] || [];
  const navigate = useNavigate();
  if (!tips.length) return null;
  const v = VARIANT[variant] || VARIANT.default;

  const flash = (el) => {
    if (!el) return;
    el.classList.add('pickup-flash');
    setTimeout(() => el.classList.remove('pickup-flash'), 1800);
  };

  const handle = (tip) => {
    if (tip.scrollTo) {
      const el = document.querySelector(tip.scrollTo);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        flash(el);
        if (tip.scrollTo === '#pickup-lever') {
          setTimeout(() => window.dispatchEvent(new CustomEvent('qsr-pickup-nudge')), 400);
        }
        return;
      }
    }
    if (onAction && tip.tab) { onAction(tip.tab); return; }
    if (tip.href) navigate(tip.href);
  };

  return (
    <Card className={v.card} data-testid={`cory-tips-${page}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className={`relative flex h-7 w-7 items-center justify-center rounded-full ${v.badge}`}>
            <Sparkles className={`w-4 h-4 ${v.icon}`} />
          </span>
          <div>
            <p className={`text-sm font-bold leading-tight ${v.title}`}>Cory suggests</p>
            <p className={`text-[11px] ${v.sub}`}>Tailored to The Daily Grill · tap to act</p>
          </div>
        </div>
        <div className="space-y-2.5">
          {tips.map((tip, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handle(tip)}
              className={`w-full text-left flex items-start gap-2.5 rounded-xl p-3 transition-colors cursor-pointer ${v.row}`}
              data-testid={`cory-tip-${page}-${i}`}
            >
              <ArrowRight className={`w-4 h-4 mt-0.5 shrink-0 ${v.icon}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${v.rowTitle}`}>{tip.t}</p>
                <p className={`text-xs mt-0.5 ${v.rowDesc}`}>{tip.d}</p>
                {tip.cta && <span className={`inline-flex items-center gap-1 text-[11px] font-semibold mt-1.5 ${v.cta}`}>{tip.cta}<ArrowRight className="w-3 h-3" /></span>}
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
